<template>
  <div>
    <navigation-top/>

    <div class="checkoutContainer">
      <div class="card orderSummary">
        <p class="orderSummaryTitle">Order summary</p>

        <div class="divider smallSpace"></div>

        <p class="orderSummarySubscriptionType">Qlerify {{ plan?.name }} Plan</p>

        <div class="d-flex justify-space-between">
          <p class="order">{{ orderCalculation }}</p>
          <p class="orderPrice">US$ {{ totalPrice.toFixed(2) }}</p>
        </div>

        <div class="divider smallSpace"></div>

        <div class="d-flex justify-space-between mb-10">
          <template v-if="plan?.name === 'Pro' && !activeSubscription?.lastSubscriptionDetails">
            <p class="order">
              <strong>Due in 28 days</strong> (Card required, no charge for 4 weeks, cancel anytime)
            </p>
          </template>
          <template v-else>
            <p class="order"><strong>Due today</strong></p>
          </template>
          <p class="orderPrice"><strong>US$ {{ totalPrice }}</strong></p>
        </div>

        <div class="elements" v-show="!isStripeLoading && !!stripe">
          <div class="payment-card">
            <v-icon size="small">mds:credit-card</v-icon>
            Card
          </div>
          <div id="address-element" class="mb-3"></div>
          <div class="mb-3">
            <label class="Label" for="Field-countryInput">Card number</label>
            <div id="card-number-element" class="Input"></div>
          </div>
          <div class="d-flex justify-space-between ga-3 mb-3">
            <div class="w-50">
              <label class="Label" for="Field-countryInput">Expiration</label>
              <div id="card-expiry-element" class="Input"></div>
            </div>
            <div class="w-50">
              <label class="Label" for="Field-countryInput">CVC</label>
              <div id="card-cvc-element" class="Input"></div>
            </div>
          </div>

          <button
            class="Button w-100"
            @click="pay"
            :disabled="loading"
          >
            <v-progress-circular
              color="white"
              indeterminate
              size="16"
              class="mr-2"
              v-if="loading"
            />
            <span v-if="plan?.name === 'Pro' && !activeSubscription?.lastSubscriptionDetails">
              Start 4 Week Free Trial
            </span>
            <span v-else>
              Purchase Now
            </span>
          </button>

          <v-alert type="info" variant="tonal" color="var(--blue-3)" icon="mdi-alert" class="mt-4">
            Orders with a billing address in <strong>Sweden</strong> are subject to <strong>25%</strong> VAT, which will
            be added at checkout. Prices
            shown do not include VAT.
          </v-alert>

          <div class="plan-badge mt-4">
            At Qlerify, we prioritize your security and peace of mind. That’s why all credit card payments on our
            platform
            are processed securely through <strong>Stripe</strong>, a globally trusted payment provider.
            With <strong>Stripe’s industry-leading encryption and PCI DSS compliance</strong>, your payment details are
            handled with the highest level of security, ensuring they are never stored or exposed on our servers.
            Feel confident knowing your sensitive information is protected every step of the way.
          </div>
        </div>
      </div>
    </div>

    <info-message-dialog
      v-if="infoMessageDialogData"
      :is-open="infoMessageDialogData.isOpen"
      :title="infoMessageDialogData.title"
      :message="infoMessageDialogData.message"
      @close="infoMessageDialogData = null"
    />
  </div>
</template>

<script>
import NavigationTop from '../components/NavigationTop.vue'
import { mapState } from 'vuex'
import { loadStripe } from '@stripe/stripe-js'
import store from '@/store'
import InfoMessageDialog from '@/components/InfoMessageDialog.vue'

export default {
  components: {
    InfoMessageDialog,
    NavigationTop
  },
  computed: {
    ...mapState({
      plan: state => state.payments.plan,
      subscriptionType: state => state.payments.subscriptionType,
      users: state => state.payments.users,
      orderCalculationText: state => state.payments.orderCalculationText,
      totalPrice: state => state.payments.totalPrice,
      currentUser: state => state.user.user,
      activeSubscription: state => state.payments.activeSubscription
    }),
    usersCount: function () {
      return this.users.length
    },
    orderCalculation: function () {
      let str = 'US$'
      str += this.plan?.value === 'basic' ? (this?.subscriptionType === 'yearly' ? '250' : '25') : this.plan?.value === 'pro' ? (this.subscriptionType === 'yearly' ? '400' : '40') : '0'
      str += ` x ${this.usersCount} ${this.usersCount === 1 ? 'user' : 'users'}`
      str += ' 1 x '
      str += this.subscriptionType === 'yearly' ? 'year' : 'month'
      return str
    }
  },
  data () {
    return {
      loading: false,
      stripe: null,
      elements: null,
      isStripeLoading: false,
      addressElement: null,
      cardNumberElement: null,
      cardExpiryElement: null,
      cardCvcElement: null,
      infoMessageDialogData: null
    }
  },
  methods: {
    async configureStripe () {
      this.isStripeLoading = true
      try {
        this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
        this.elements = this.stripe.elements()

        // Address Element
        this.addressElement = this.elements.create('address', {
          mode: 'billing',
          autocomplete: { mode: 'google_maps_api', apiKey: 'AIzaSyDkyS96K5XC-Yt6dHHzU5KilI2xw4Nc5do' },
          defaultValues: {
            name: `${this.currentUser?.firstname} ${this.currentUser?.lastname}`
          }
        })
        this.addressElement.mount('#address-element')
        // CardNumber Element
        this.cardNumberElement = this.elements.create('cardNumber')
        this.cardNumberElement.mount('#card-number-element')
        // CardExpiry Element
        this.cardExpiryElement = this.elements.create('cardExpiry')
        this.cardExpiryElement.mount('#card-expiry-element')
        // CardCvc Element
        this.cardCvcElement = this.elements.create('cardCvc')
        this.cardCvcElement.mount('#card-cvc-element')

        this.isStripeLoading = false
      } catch (err) {
        console.log(err)
        this.isStripeLoading = false
        this.$router.push({ path: '/plans' })
      }
    },
    async pay () {
      this.loading = true
      const address = await this.addressElement.getValue()

      const { error: submitError } = await this.elements.submit()
      if (submitError || this.cardNumberElement?._invalid || this.cardExpiryElement?._invalid || this.cardCvcElement?._invalid) {
        let message = ''

        if (submitError) {
          message = submitError.message
        } else if (this.cardNumberElement?._invalid) {
          message = 'Invalid card number'
        } else if (this.cardExpiryElement?._invalid) {
          message = 'Invalid card expiry date'
        } else if (this.cardCvcElement?._invalid) {
          message = 'Invalid card CVC'
        } else {
          message = 'Something went wrong'
        }

        this.loading = false
        store.commit('snackbar/showMessage', {
          content: message,
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
        return
      }

      const { error, paymentMethod } = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.cardNumberElement,
        billing_details: {
          email: this.currentUser?.email,
          name: `${this.currentUser?.firstname} ${this.currentUser?.lastname}`,
          address: {
            city: address.value.address.city,
            country: address.value.address.country,
            line1: address.value.address.line1,
            state: address.value.address.state,
            postal_code: address.value.address.postal_code,
            ...(!!address.value.address.line2 && { line2: address.value.address.line2 })
          }
        }
      })

      if (error) {
        console.error(error)
        this.loading = false
        return store.commit('snackbar/showMessage', {
          content: error.message,
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }

      const addPaymentMethodParams = {
        paymentMethodId: paymentMethod.id,
        newCustomerInfo: {
          name: `${this.currentUser?.firstname} ${this.currentUser?.lastname}`,
          email: this.currentUser?.email
        }
      }

      const addPaymentMethodResponse = await store.dispatch('payments/addPaymentMethod', addPaymentMethodParams)

      if (addPaymentMethodResponse.success) {
        const createSubscriptionParams = {
          subscriptionPriceId: this.plan.prices[this.subscriptionType],
          users: this.users,
          stripe: this.stripe
        }
        const createSubscriptionResponse = await store.dispatch('payments/createSubscription', createSubscriptionParams)

        if (createSubscriptionResponse.success) {
          await store.dispatch('payments/getActiveSubscription')
          await store.dispatch('payments/getPayerUserSubscription')

          if (this.activeSubscription?.subscriptionDetails?.status === 'trialing') {
            this.$router.push({ path: '/dashboard', query: { plan: 'trial' } })
          } else {
            this.$router.push({ path: '/dashboard', query: { plan: this.plan?.value } })
          }
          this.$store.commit('snackbar/showMessage', { content: 'You have subscribed to Qlerify plan!' }, { root: true })
          console.log('trackPlanPurchased', this.users[0], this.plan?.value)
          window.trackPlanPurchased({ email: this.users[0], plan: this.plan?.value })
        } else {
          store.commit('snackbar/showMessage', {
            content: createSubscriptionResponse.message,
            timeout: 6000,
            color: 'red',
            location: 'center center'
          }, { root: true })
        }
      } else {
        this.infoMessageDialogData = {
          message: addPaymentMethodResponse.message,
          isOpen: true
        }
      }
      this.loading = false
    }
  },
  mounted () {
    if (!this.subscriptionType || !this.plan || this.usersCount < 1) {
      this.$router.push('/plans')
    }

    this.configureStripe()
  }
}
</script>

<style scoped>
.checkoutContainer {
  gap: 48px;
  display: flex;
  margin-top: 32px;
  margin-bottom: 48px;
  justify-content: center;
  align-items: flex-start;
}

.card {
  padding: 24px !important;
  background-color: var(--white, #ffffff);
}

.orderSummary {
  width: 581px;
}

.divider {
  width: 100%;
  height: 1px;
  margin: 32px 0;
  background-color: var(--dark-1);

  &.smallSpace {
    margin: 20px 0;
  }
}

.orderSummaryTitle {
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  color: var(--text-dark, #1D1F22);
}

.orderSummarySubscriptionType {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;
  color: var(--text-dark, #1D1F22);
}

.plan-badge {
  gap: 8px;
  padding: 8px;
  font-size: 12px;
  font-weight: 400;
  border-radius: 8px;
  align-items: center;
  color: var(--dark-4, #363A40);
  background: var(--light-2, #F5F2F0);
}

.elements {
  --accessibleColorOnColorPrimary: #fff;
  --p-colorPrimaryHover: #0b81fa;
  --p-colorPrimaryActive: #228cfa;
  --p-colorPrimaryDisabled: #d9d9d9;
  --p-colorPrimaryDisabledText: #8d8d8d;
  --p-colorBackgroundDisabled: #f2f2f2;
  --p-colorBackgroundDisabledDeemphasize05: #e6e6e6;
  --p-colorBackgroundDeemphasize10: #e6e6e6;
  --colorTextPlaceholder: #757680;
  --fontWeightMedium: 600;
  --colorPrimary: #0570de;
  --colorBackground: #fff;
  --colorText: #30313d;
  --borderRadius: 5px;
  --colorPrimaryText: var(--accessibleColorOnColorPrimary);
  --fontSizeSm: 14.88px;
  --p-spacingXs: 1px;
  --p-spacingSm: 2px;
  --p-spacing1: 4px;
  --p-spacing2: 6px;
  --p-spacing3: 12px;
  --fontFamily: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;

  .payment-card {
    gap: 4px;
    width: 140px;
    height: 64px;
    padding: 12px;
    font-size: 14px;
    font-weight: 600;
    border-radius: 5px;
    margin-bottom: 12px;
    display: inline-flex;
    flex-direction: column;
    background: var(--white);
    color: var(--colorPrimary);
    border: 1px solid var(--colorPrimary);
    box-shadow: 0 0 0 1px var(--colorPrimary), 0 1px 1px 0 rgba(0, 0, 0, 0.08);
  }

  .Input {
    padding: var(--p-spacing3);
    background-color: var(--colorBackground);
    border-radius: var(--borderRadius);
    transition: background 0.15s ease, border 0.15s ease, box-shadow 0.15s ease, color 0.15s ease;
    border: 1px solid var(--p-colorBackgroundDeemphasize10);
    box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02);
  }

  .Input::placeholder {
    color: var(--colorTextPlaceholder);
  }

  .Input:disabled {
    background-color: var(--p-colorBackgroundDisabled);
    color: var(--colorTextPlaceholder);
    border-color: var(--p-colorBackgroundDisabledDeemphasize05);
    box-shadow: none;
  }

  .Label {
    display: block;
    touch-action: manipulation;
    margin-bottom: var(--p-spacing1);
    font-size: var(--fontSizeSm);
    transition: transform 0.5s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.5s cubic-bezier(0.19, 1, 0.22, 1);
    font-family: var(--fontFamily);
  }

  .Button {
    padding: var(--p-spacing3);
    background-color: var(--colorPrimary);
    font-weight: var(--fontWeightMedium);
    color: var(--colorPrimaryText);
    border-radius: var(--borderRadius);
    transition: background 0.15s ease, color 0.15s ease, border 0.15s ease, box-shadow .15s ease;
  }

  .Button:hover {
    background-color: var(--p-colorPrimaryHover);
  }

  .Button:active {
    background-color: var(--p-colorPrimaryActive);
  }

  .Button:disabled {
    background-color: var(--p-colorPrimaryDisabled);
    color: var(--p-colorPrimaryDisabledText);
  }
}
</style>
